import { useMutation, useQuery } from '@apollo/client';
import { ColumnsType } from 'antd/es/table';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { STRATEGY_MODELS, STRATEGY_TYPES } from '../constants';
import { tableSortingMethods } from '../../../../../utils/tableSortingMethods';

import { Footer, TitleBar } from '../../../../../components';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import {
  DocumentLinks,
  OwnersInformation,
  SummaryContent,
} from '../../components';

import { FIND_STRATEGY_FACILITY_BY_STRATEGY_ID } from '../../../../dataInputs/queries/strategies';
import {
  CREATE_FACILITY,
  DELETE_FACILITY,
  FIND_ALL_FACILITIES_BY_STRATEGY_FACILITY,
  GET_SECTION_OWNER,
  UPDATE_FACILITY,
  UPDATE_SECTION_OWNER,
  UPDATE_STRATEGY_FACILITY,
} from '../../../queries';
import { GET_DOCUMENT_LINKS_BY_STRATEGY } from '../../../queries/documentLink';
import { formFields } from './formFields';
import { cleanUpObject } from '../../../../../utils';
import QueryErrorBoundary from '../../../../../components/error-handling';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

type StrategyFacilityProps = {
  strategyType?: string;
};

const StrategyFacility = ({
  strategyType = 'processArea',
}: StrategyFacilityProps) => {
  const { locationId, processAreaId, strategyId } = useParams();
  const [searchParams] = useSearchParams();

  const locationName = searchParams.get('locationName');
  const strategyLength = searchParams.get('strategyLength');

  let strategyName;

  if (strategyType === 'processArea') {
    strategyName = searchParams.get('name');
  }

  const subtitle =
    strategyType === 'processArea'
      ? `${locationName} // ${strategyName} // ${strategyLength} Month Strategy`
      : `${locationName} // ${strategyLength} Month Strategy`;

  const backPath =
    strategyType === 'processArea'
      ? `/process-areas-and-strategies/${locationId}/${processAreaId}`
      : `/strategy-summary/${locationId}/${strategyId}?locationName=${locationName}&strategyLength=${strategyLength}`;

  const OPERATION_NAMES = {
    SECTION_OWNER: 'GetSectionOwnerByStrategy',
    FACILITIES: 'FindAllFacilitiesByStrategyFacility',
    STRATEGY_FACILITY: 'FindStrategyFacilityByStrategy',
    DOCUMENTS: 'FindAllByStrategyFacility',
  };

  const {
    data: strategyFacility,
    loading: strategyFacilityLoading,
    error: strategyFacilityError,
  } = useQuery(FIND_STRATEGY_FACILITY_BY_STRATEGY_ID, {
    variables: { uuid: strategyId },
    skip: !strategyId,
  });

  const strategyFacilityData = strategyFacility?.findStrategyFacilityByStrategy;

  const { data: strategyOwner, error: strategyOwnerError } = useQuery(
    GET_SECTION_OWNER,
    {
      variables: { uuid: strategyId, strategyType: STRATEGY_TYPES.FACILITIES },
      skip: !strategyId,
    },
  );

  const sectionOwner = strategyOwner?.findSectionOwnerByStrategy;

  const {
    data: facilities,
    loading: facilitiesLoading,
    error: facilitiesError,
  } = useQuery(FIND_ALL_FACILITIES_BY_STRATEGY_FACILITY, {
    variables: {
      uuid: strategyFacilityData?.uuid,
    },
    skip: !strategyFacilityData?.uuid,
  });

  const facilitiesData = facilities?.findAllFacilitiesByStrategyFacility;

  const { data: documents, error: documentsError } = useQuery(
    GET_DOCUMENT_LINKS_BY_STRATEGY,
    {
      variables: {
        uuid: strategyFacilityData?.uuid,
      },
      skip: !strategyFacilityData?.uuid,
    },
  );

  const documentsData = documents?.findAllByStrategyFacility;

  const [updateOwner] = useMutation(UPDATE_SECTION_OWNER, {
    refetchQueries: [OPERATION_NAMES.SECTION_OWNER],
  });

  const [createFacility] = useMutation(CREATE_FACILITY, {
    refetchQueries: [OPERATION_NAMES.FACILITIES],
  });

  const [updateFacility] = useMutation(UPDATE_FACILITY, {
    refetchQueries: [OPERATION_NAMES.FACILITIES],
  });

  const [deleteFacility] = useMutation(DELETE_FACILITY, {
    refetchQueries: [OPERATION_NAMES.FACILITIES],
  });

  const [updateStrategyFacility] = useMutation(UPDATE_STRATEGY_FACILITY, {
    refetchQueries: [OPERATION_NAMES.STRATEGY_FACILITY],
  });

  const columns: ColumnsType<any> = [
    {
      key: 'name',
      title: 'Facility/Utility',
      width: 100,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: ({ name }) => (
        <span className="custom-table--column custom-table--column-primary">
          {name}
        </span>
      ),
    },
    {
      key: 'available',
      title: 'Available',
      width: 100,
      align: 'center',
      sorter: (a, b) => tableSortingMethods.boolean(a.available, b.available),
      render: ({ available }) =>
        available !== undefined && available !== null ? (
          <span className="custom-table--column">
            {available ? 'Yes' : 'No'}
          </span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'adequate',
      title: 'Adequate',
      width: 100,
      align: 'center',
      sorter: (a, b) => tableSortingMethods.boolean(a.adequate, b.adequate),
      render: ({ adequate }) =>
        adequate !== undefined && adequate !== null ? (
          <span className="custom-table--column">
            {adequate ? 'Yes' : 'No'}
          </span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'rampUpTime',
      title: 'Ramp-Up Time (months)',
      width: 100,
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.rampUpTime, b.rampUpTime),
      render: ({ rampUpTime }) =>
        rampUpTime ? (
          <div className="custom-table--column">{rampUpTime}</div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'internalContact',
      title: 'Internal Contact',
      width: 100,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.contactFirstName, b.contactFirstName),
      render: ({ contactFirstName, contactLastName, email }) =>
        contactFirstName || contactLastName || email ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>
              {contactFirstName} {contactLastName}
            </span>
            <span>{email}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const modifyVariables = ({ values, type }: ModifyVariablesArgs) => {
    const {
      adequate,
      available,
      sectionOwnerfirstName,
      sectionOwnerlastName,
      ...restValues
    } = values;
    const commonFields = {
      ...restValues,
      adequate: adequate && adequate === 'true',
      available: available && available === 'true',
      contactFirstName: values?.sectionOwnerfirstName,
      contactLastName: values?.sectionOwnerlastName,
      cost: parseFloat('0'),
      rampUpTime: String(values?.rampUpTime),
    };

    const cleanedValues = cleanUpObject(commonFields);

    if (type === 'create') {
      return {
        ...cleanedValues,
        strategyFacility: {
          uuid: strategyFacilityData?.uuid,
        },
      };
    }
    return cleanedValues;
  };

  return (
    <>
      <TitleUpdater title={`Facilities`} />
      <TitleBar title="BCP Hub" subtitle={subtitle} />
      <section className="flex flex-col gap-4">
        <OwnersInformation
          title="Facilities Section Owner"
          data={sectionOwner}
          updateGQL={updateOwner}
          error={strategyOwnerError}
        />
        <QueryErrorBoundary error={facilitiesError}>
          <TableV3
            title="Facilities/Utilities Requirements"
            columns={columns}
            data={facilitiesData}
            loading={facilitiesLoading}
            editable
            formFields={formFields}
            createGQL={createFacility}
            updateGQL={updateFacility}
            deleteGQL={deleteFacility}
            modifyVariables={modifyVariables}
            exportExcelColumns={baseColumns}
          />
        </QueryErrorBoundary>
        <section className="w-full grid xl:grid-cols-2 gap-4">
          <SummaryContent
            title="Facilities Commentary"
            data={strategyFacilityData}
            loading={strategyFacilityLoading}
            error={strategyFacilityError}
            updateGQL={updateStrategyFacility}
          />
          <DocumentLinks
            data={documentsData}
            error={documentsError}
            parentUuid={strategyFacilityData?.uuid}
            queryToUpdate={OPERATION_NAMES.DOCUMENTS}
            model={STRATEGY_MODELS.FACILITIES}
          />
        </section>
        <Footer backPath={backPath} />
      </section>
    </>
  );
};

export default StrategyFacility;
